import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@medikei/ui';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  TrendingUp,
  Users,
  Calendar,
  DollarSign,
  Activity,
  Clock,
} from 'lucide-react';

interface DashboardMetrics {
  totalDoctors: number;
  totalPatients: number;
  totalAppointments: number;
  totalRevenue: number;
  appointmentsToday: number;
  appointmentsThisWeek: number;
  appointmentsThisMonth: number;
  revenueToday: number;
  revenueThisWeek: number;
  revenueThisMonth: number;
  expressQueueSize: number;
  averageWaitTime: number;
  topSpecialties: Array<{ name: string; count: number }>;
  recentAppointments: Array<{
    id: string;
    patientName: string;
    doctorName: string;
    specialty: string;
    scheduledAt: string;
    status: string;
    type: string;
    price: number;
  }>;
}

interface RevenueMetrics {
  total: number;
  today: number;
  thisWeek: number;
  thisMonth: number;
  daily: Array<{ date: string; amount: number }>;
  byPaymentMethod: Array<{ method: string; amount: number; count: number }>;
}

interface ConsultationMetrics {
  total: number;
  completed: number;
  cancelled: number;
  noShow: number;
  byStatus: Array<{ status: string; count: number }>;
  byType: Array<{ type: string; count: number }>;
}

interface ExpressMetrics {
  queueSize: number;
  totalRequests: number;
  acceptedRequests: number;
  rejectedRequests: number;
  averageWaitTime: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

export function AdminAnalyticsPage() {
  const { data: dashboard, isLoading: dashboardLoading } = useQuery<DashboardMetrics>({
    queryKey: ['analytics', 'dashboard'],
    queryFn: () => fetch('/api/v1/analytics/dashboard').then((res) => res.json()),
  });

  const { data: revenue } = useQuery<RevenueMetrics>({
    queryKey: ['analytics', 'revenue'],
    queryFn: () => fetch('/api/v1/analytics/revenue').then((res) => res.json()),
  });

  const { data: consultations } = useQuery<ConsultationMetrics>({
    queryKey: ['analytics', 'consultations'],
    queryFn: () => fetch('/api/v1/analytics/consultations').then((res) => res.json()),
  });

  const { data: express } = useQuery<ExpressMetrics>({
    queryKey: ['analytics', 'express'],
    queryFn: () => fetch('/api/v1/analytics/express').then((res) => res.json()),
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  };

  if (dashboardLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Analytics</h1>
        <span className="text-sm text-gray-500">
          Última atualização: {new Date().toLocaleString('pt-BR')}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Médicos</p>
              <p className="text-2xl font-bold">
                {dashboard?.totalDoctors.toLocaleString('pt-BR') || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Pacientes</p>
              <p className="text-2xl font-bold">
                {dashboard?.totalPatients.toLocaleString('pt-BR') || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Consultas</p>
              <p className="text-2xl font-bold">
                {dashboard?.totalAppointments.toLocaleString('pt-BR') || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Receita Total</p>
              <p className="text-2xl font-bold">
                {formatCurrency(dashboard?.totalRevenue || 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Receita Diária
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenue?.daily || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getDate()}/${date.getMonth() + 1}`;
                    }}
                  />
                  <YAxis tickFormatter={(value) => `R$ ${value}`} />
                  <Tooltip
                    formatter={(value) => [formatCurrency(Number(value)), 'Receita']}
                    labelFormatter={(label) => new Date(String(label)).toLocaleDateString('pt-BR')}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-500" />
              Consultas por Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={consultations?.byStatus || []}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, value }: { name?: string; value?: number }) => `${name ?? ''}: ${value ?? 0}`}
                  >
                    {(consultations?.byStatus || []).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              Filas Expressas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Na Fila</span>
                <span className="text-2xl font-bold">{express?.queueSize || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Requests</span>
                <span className="font-medium">{express?.totalRequests || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Aceitos</span>
                <span className="font-medium text-green-600">
                  {express?.acceptedRequests || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Rejeitados</span>
                <span className="font-medium text-red-600">
                  {express?.rejectedRequests || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tempo Médio Espera</span>
                <span className="font-medium">
                  {express?.averageWaitTime || 0} min
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-500" />
              Consultas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Hoje</span>
                <span className="text-2xl font-bold">
                  {dashboard?.appointmentsToday || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Esta Semana</span>
                <span className="font-medium">
                  {dashboard?.appointmentsThisWeek || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Este Mês</span>
                <span className="font-medium">
                  {dashboard?.appointmentsThisMonth || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Concluídas</span>
                <span className="font-medium text-green-600">
                  {consultations?.completed || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Canceladas</span>
                <span className="font-medium text-red-600">
                  {consultations?.cancelled || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              Receita
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Hoje</span>
                <span className="text-xl font-bold">
                  {formatCurrency(revenue?.today || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Esta Semana</span>
                <span className="font-medium">
                  {formatCurrency(revenue?.thisWeek || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Este Mês</span>
                <span className="font-medium">
                  {formatCurrency(revenue?.thisMonth || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total</span>
                <span className="font-medium">
                  {formatCurrency(revenue?.total || 0)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Especialidades Mais Populares</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dashboard?.topSpecialties || []}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                  />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366F1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Últimas Consultas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {dashboard?.recentAppointments.map((apt) => (
                <div
                  key={apt.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{apt.patientName}</p>
                    <p className="text-sm text-gray-500">
                      Dr. {apt.doctorName} - {apt.specialty}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatDate(apt.scheduledAt)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(apt.price)}</p>
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        apt.status === 'FINISHED'
                          ? 'bg-green-100 text-green-700'
                          : apt.status === 'CANCELLED'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {apt.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!dashboard?.recentAppointments || dashboard.recentAppointments.length === 0) && (
                <p className="text-gray-500 text-center py-4">Nenhuma consulta recente</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
